import { Link } from 'react-router-dom';
import type { Episode } from '../types';
import { formatCount, formatFullDateRange } from '../lib/format';

interface Props {
  episode: Episode;
  /** `rail` is the compact horizontal-scroll card; `grid` is the larger tile. */
  variant?: 'rail' | 'grid';
  showProgress?: boolean;
  index?: number;
}

/**
 * The atomic unit of the browsing UI. Deliberately image-first: the cover
 * dominates, metadata is secondary. This is what makes it feel like a
 * streaming service rather than a file browser.
 *
 * Information hierarchy (see ARCHITECTURE.md §4): the generated episode title is
 * "Murree · June 14–16, 2019", which is long and wraps awkwardly on a card. So
 * the card splits it into its real components —
 *
 *     Murree                  <- location, only when actually known
 *     June 14–16, 2019        <- the date range from the event
 *     63 photos • 3 videos    <- accurate counts from the API
 *
 * Nothing is invented: the location line is hidden when the event has none.
 */
export default function EpisodeCard({ episode, variant = 'rail', showProgress = false, index }: Props) {
  const isGrid = variant === 'grid';
  const hasCover = Boolean(episode.coverUrl);
  const dateRange = formatFullDateRange(episode.startDate, episode.endDate);

  // Hierarchy:
  //   line 1 (eyebrow)  location, only when the event actually has one
  //   line 2 (heading)  the date range — or the episode title when there is no
  //                     location, because a custom title ("Summer in Murree")
  //                     is more meaningful than a bare date
  const heading = episode.locationName ? dateRange || episode.title : episode.title;

  const photos = episode.photoCount ?? 0;
  const videos = episode.videoCount ?? 0;
  const countParts: string[] = [];
  if (photos > 0) countParts.push(`${formatCount(photos)} ${photos === 1 ? 'photo' : 'photos'}`);
  if (videos > 0) countParts.push(`${formatCount(videos)} ${videos === 1 ? 'video' : 'videos'}`);
  const counts = countParts.length > 0 ? countParts.join(' • ') : `${formatCount(episode.mediaCount)} memories`;

  return (
    <Link
      to={`/episode/${episode.id}`}
      className="focus-ring group relative block shrink-0 overflow-hidden rounded-lg bg-ink-850 ring-1 ring-white/5 transition-all duration-300 hover:ring-white/20"
      style={{
        width: isGrid ? '100%' : 'clamp(200px, 26vw, 300px)',
        // Staggered entrance, capped so long rails do not feel slow.
        animationDelay: `${Math.min(index ?? 0, 8) * 45}ms`,
      }}
    >
      <div className={`relative overflow-hidden bg-ink-800 ${isGrid ? 'aspect-[16/10]' : 'aspect-video'}`}>
        {hasCover ? (
          <img
            src={episode.coverUrl!}
            alt=""
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
          />
        ) : (
          <div className="flex h-full items-center justify-center bg-gradient-to-br from-ink-800 to-ink-950 text-white/15">
            <svg viewBox="0 0 24 24" className="h-10 w-10" fill="currentColor">
              <path d="M4 5h16v14H4z" opacity="0.25" />
            </svg>
          </div>
        )}

        {/* Bottom scrim keeps the text legible over any photo. */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />

        <div className="absolute inset-x-0 bottom-0 p-3 sm:p-3.5">
          {/* Location — only rendered when the event actually has one. */}
          {episode.locationName && (
            <div className="mb-0.5 text-[10px] font-semibold tracking-wide text-accent-soft uppercase">
              {episode.locationName}
            </div>
          )}

          <h3 className="line-clamp-1 text-sm leading-tight font-semibold text-white drop-shadow sm:text-[15px]">
            {heading}
          </h3>

          <div className="mt-1 flex items-center gap-2 text-[11px] text-white/55">
            <span className="truncate">{counts}</span>
            {episode.durationSeconds > 0 && (
              <>
                <span className="text-white/25">•</span>
                <span className="shrink-0">{formatCount(Math.round(episode.durationSeconds))}s</span>
              </>
            )}
          </div>
        </div>

        {/* Hover play affordance — the primary call to action. */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/45 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent shadow-glow">
            <svg viewBox="0 0 24 24" className="ml-0.5 h-6 w-6 text-white" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
        </div>

        {showProgress && episode.progress != null && episode.progress > 0 && (
          <div className="absolute inset-x-0 bottom-0 h-1 bg-white/15">
            <div
              className="h-full bg-accent"
              style={{ width: `${Math.min(100, episode.progress * 100)}%` }}
            />
          </div>
        )}
      </div>
    </Link>
  );
}